/**
 * Main editable cell input dispatcher component.
 * Routes to the appropriate input component based on field type.
 *
 * Relation inputs  → inputs/RelationInputs.tsx
 * Scalar inputs    → inputs/ScalarInputs.tsx
 */

import React, { useMemo } from "react";
import {
  createKeyDownHandler,
  createBlurSaveHandler,
} from "../../utils/editable-cell-edit-handlers";
import { getInputType } from "./utils/input-factory";
import {
  MultiUserInput,
  UserInput,
  TicketInput,
  CompanyInput,
  ContactInput,
  EmailAliasesInput,
} from "./inputs/RelationInputs";
import {
  SelectInput,
  DateInput,
  DateTimeInput,
  ProbabilityInput,
  NumberInput,
  BooleanInput,
  TextAreaInput,
  TextInput,
} from "./inputs/ScalarInputs";
import { DriveFolderInput } from "./inputs/DriveFolderInput";
import type { EditableCellEditInputsProps } from "./types";
import type { BaseEntity } from "../../types/index";

/**
 * Renders the appropriate input for a field based on its type.
 */
export function EditableCellEditInputs<T extends BaseEntity = BaseEntity>({
  value,
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onSave,
  onCancel,
  setEditValue,
  onInlineEdit,
  cellKey,
}: EditableCellEditInputsProps<T>): React.ReactNode {
  const inputType = useMemo(() => getInputType(fieldId, fieldConfig), [fieldId, fieldConfig]);

  const keyDownHandler = useMemo(
    () => createKeyDownHandler({ onSave, onCancel, rowId, fieldId }),
    [onSave, onCancel, rowId, fieldId]
  );

  const blurSaveHandler = useMemo(
    () => createBlurSaveHandler({ onSave, rowId, fieldId, cellKey }),
    [onSave, rowId, fieldId, cellKey]
  );

  // Shared props passed to every input component
  const sharedProps = {
    value,
    fieldConfig,
    rowId,
    fieldId,
    editValue,
    data,
    onSave,
    onCancel,
    setEditValue,
    onInlineEdit,
    cellKey,
    keyDownHandler,
    blurSaveHandler,
  };

  // --- Relation inputs ---
  if (inputType === "multi-user") return <MultiUserInput {...sharedProps} />;
  if (inputType === "user") return <UserInput {...sharedProps} />;
  if (inputType === "ticket") return <TicketInput {...sharedProps} />;
  if (inputType === "company") return <CompanyInput {...sharedProps} />;
  if (inputType === "contact") return <ContactInput {...sharedProps} />;
  if (inputType === "email-aliases") return <EmailAliasesInput {...sharedProps} />;
  if (inputType === "drive-folder") return <DriveFolderInput {...sharedProps} />;

  // --- Scalar inputs ---
  if (inputType === "select") return <SelectInput {...sharedProps} />;
  if (inputType === "date") return <DateInput {...sharedProps} />;
  if (inputType === "datetime") return <DateTimeInput {...sharedProps} />;
  if (inputType === "probability") return <ProbabilityInput {...sharedProps} />;
  if (inputType === "number") return <NumberInput {...sharedProps} />;
  if (inputType === "boolean") return <BooleanInput {...sharedProps} />;
  if (inputType === "textarea") return <TextAreaInput {...sharedProps} />;

  // Default: plain text
  return <TextInput {...sharedProps} />;
}
